import { useEffect, useState } from 'react';

import {

  getPrompts,
  getPromptsPage,

  getAllCategories,

  deletePrompt,

  hidePrompt,

  restorePrompt,

  getUsers,
  getUsersPage,
  
  createGlobalCategory,

  updateCategoryStatus

} from '../../utils/firebaseService';
import { Prompt, Category } from '../../types';
import AdminSidebar from '../../admin/componentes/AdminSidebar';
import DashboardSection from '../../admin/sections/DashboardSection';
import UsersSection from '../../admin/sections/UsersSection';
import PromptsSection from '../../admin/sections/PromptsSection';
import CategoriesSection from '../../admin/sections/CategoriesSection';
import type {UserData} from '../../types/UserData';
import { auth } from '../../config/firebase';
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import Toast, { ToastTone } from '../../components/feedback/Toast';
import ErrorModal from '../../components/feedback/ErrorModal';
import LoadingScreen from '../../components/feedback/LoadingScreen';



const AdminPage = () => {
  //STATES
  const [prompts, setPrompts] =
  useState<Prompt[]>([]);
  const [categories, setCategories] =
  useState<Category[]>([]);
  const [newCategoryName, setNewCategoryName] =
  useState('');
  const [creatingCategory, setCreatingCategory] =
  useState(false);
  const [loading, setLoading] =
  useState(true);
  const [users, setUsers] =
  useState<UserData[]>([]);
  const [usersCursor, setUsersCursor] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [promptsCursor, setPromptsCursor] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMoreUsers, setHasMoreUsers] = useState(false);
  const [hasMorePrompts, setHasMorePrompts] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [feedback, setFeedback] = useState<{ message: string; tone: ToastTone } | null>(null);
  const [promptToDelete, setPromptToDelete] = useState<string | null>(null);

//DADOS DERIVADOS
  const totalPrompts =
  prompts.length;

  const publicPrompts =
  prompts.filter(
    (p) => p.isPublic
  ).length;

  const privatePrompts =
  prompts.filter(
    (p) => !p.isPublic
  ).length;

  const [activeSection, setActiveSection] =
  useState<
    'dashboard'
    | 'users'
    | 'prompts'
    | 'categories'
  >('dashboard');

  const loadUsers = async (append = false) => {

  try {

      const page = await getUsersPage(50, append ? usersCursor : null);

    setUsers((currentUsers) => append ? [...currentUsers, ...page.users] : page.users);
    setUsersCursor(page.cursor);
    setHasMoreUsers(page.hasMore);

  } catch (error) {

    console.error(error);
    setFeedback({ message: 'Não foi possível carregar os usuários.', tone: 'error' });

  }
  };

  const loadPrompts = async (append = false) => {
    const page = await getPromptsPage({}, 50, append ? promptsCursor : null);
    setPrompts((currentPrompts) => append ? [...currentPrompts, ...page.prompts] : page.prompts);
    setPromptsCursor(page.cursor);
    setHasMorePrompts(page.hasMore);
  };

  const loadMoreUsers = async () => {
    if (!hasMoreUsers || loadingMore) return;
    setLoadingMore(true);
    try { await loadUsers(true); } finally { setLoadingMore(false); }
  };

  const loadMorePrompts = async () => {
    if (!hasMorePrompts || loadingMore) return;
    setLoadingMore(true);
    try { await loadPrompts(true); } finally { setLoadingMore(false); }
  };

const loadDashboard = async () => {
  const [promptsData, categoriesData, usersData] = await Promise.all([
    getPrompts(),
    getAllCategories(),
    getUsers(),
  ]);

  setPrompts(promptsData);
  setCategories(categoriesData.filter((category) => category.type === 'global'));
  setUsers(usersData);
};

const loadActiveSection = async () => {
  setLoading(true);

  try {
    if (activeSection === 'dashboard') {
      await loadDashboard();
    } else if (activeSection === 'users') {
      await loadUsers();
    } else if (activeSection === 'prompts') {
      await loadPrompts();
    } else {
      const categoriesData = await getAllCategories();
      setCategories(categoriesData.filter((category) => category.type === 'global'));
    }
  } catch (error) {
    console.error(error);
    setFeedback({ message: 'Não foi possível carregar os dados do painel.', tone: 'error' });
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  loadActiveSection();
}, [activeSection]);


  //HANDLERS
const handleDeletePrompt =
  async (id: string) => {

    try {

      const adminId = auth.currentUser?.uid;
      if (!adminId) {
        throw new Error('Administrador não autenticado.');
      }

      await deletePrompt(
        id,
        adminId
      );

      setPrompts((prev) =>

        prev.filter(
          (prompt) =>
            prompt.id !== id
        )
      );
      setFeedback({ message: 'Prompt excluído com sucesso.', tone: 'success' });

    } catch (error) {

      console.error(error);

      setFeedback({ message: 'Erro ao excluir prompt.', tone: 'error' });
    }
};

const handleHidePrompt =
  async (id: string) => {

    try {

      await hidePrompt(id);

      setPrompts((prev) =>

        prev.map((prompt) =>

          prompt.id === id

            ? {
                ...prompt,
                isHidden: true
              }

            : prompt
        )
      );

    } catch (error) {

      console.error(error);

      setFeedback({ message: 'Erro ao ocultar prompt.', tone: 'error' });
    }
};

const handleRestorePrompt =
  async (id: string) => {

    try {

      await restorePrompt(id);

      setPrompts((prev) =>

        prev.map((prompt) =>

          prompt.id === id

            ? {
                ...prompt,
                isHidden: false
              }

            : prompt
        )
      );

    } catch (error) {

      console.error(error);

      setFeedback({ message: 'Erro ao restaurar prompt.', tone: 'error' });
    }
};

const handleCreateCategory =
  async () => {

    if (!newCategoryName.trim()) {

      setFeedback({ message: 'Digite o nome da categoria.', tone: 'error' });

      return;
    }

    try {

      setCreatingCategory(true);

      const categoryId =
        await createGlobalCategory(
          newCategoryName
        );

      setCategories((prev) => [

        ...prev,

        {

          id: categoryId,

          name: newCategoryName.trim(),

          searchName:
            newCategoryName
              .trim()
              .toLowerCase(),

          type: 'global',

          isActive: true

        }

      ]);

      setNewCategoryName('');

    } catch (error: any) {

      console.error(error);

      setFeedback({ message: error?.message || 'Erro ao criar categoria.', tone: 'error' });

    } finally {

      setCreatingCategory(false);
    }
};

const handleToggleCategory =
  async (
    categoryId: string,
    currentStatus: boolean
  ) => {

    try {

      await updateCategoryStatus(
        categoryId,
        !currentStatus
      );

      setCategories((prev) =>

        prev.map((category) =>

          category.id === categoryId

            ? {
                ...category,
                isActive: !currentStatus
              }

            : category

        )

      );

    } catch (error) {

      console.error(error);

      setFeedback({ message: 'Erro ao atualizar categoria.', tone: 'error' });
    }
};

 const requestDeletePrompt = async (id: string) => {
   setPromptToDelete(id);
 };

 if (loading) {

  return (

    <div className="min-h-screen bg-[#020817] flex items-center justify-center text-white">

      <LoadingScreen label="Carregando painel..." />

    </div>

  );
}

//RETURN
  return (

  <div className="min-h-screen bg-[#020817] text-white flex">
  {feedback && <Toast message={feedback.message} tone={feedback.tone} onClose={() => setFeedback(null)} />}
  <ErrorModal
    isOpen={Boolean(promptToDelete)}
    title="Excluir prompt"
    message="Deseja realmente excluir este prompt?"
    confirmLabel="Excluir"
    destructive
    onClose={() => setPromptToDelete(null)}
    onConfirm={() => {
      const id = promptToDelete;
      setPromptToDelete(null);
      if (id) void handleDeletePrompt(id);
    }}
  />
<AdminSidebar
  activeSection={activeSection}
  setActiveSection={setActiveSection}
/>

<main className="flex-1 p-10 overflow-y-auto">

  {activeSection === 'dashboard' && (

    <DashboardSection
      totalPrompts={totalPrompts}
      publicPrompts={publicPrompts}
      privatePrompts={privatePrompts}
      totalUsers={users.length}
      activeCategories={
      categories.filter(c => c.isActive !== false).length}
      recentUsers={[...users].slice(0, 5)}
      recentPrompts={[...prompts].slice(0, 5)}
    />

  )}

  {activeSection === 'users' && (

    <UsersSection
  users={users}
  refreshUsers={loadUsers}
  hasMore={hasMoreUsers}
  onLoadMore={loadMoreUsers}
  loadingMore={loadingMore}
/>

  )}

  {activeSection === 'prompts' && (

    <PromptsSection
      prompts={prompts}
      hasMore={hasMorePrompts}
      onLoadMore={loadMorePrompts}
      loadingMore={loadingMore}
      handleHidePrompt={handleHidePrompt}
      handleRestorePrompt={handleRestorePrompt}
      handleDeletePrompt={requestDeletePrompt}
    />

  )}

  {activeSection === 'categories' && (

    <CategoriesSection
      categories={categories}
      newCategoryName={newCategoryName}
      setNewCategoryName={setNewCategoryName}
      creatingCategory={creatingCategory}
      handleCreateCategory={handleCreateCategory}
      handleToggleCategory={handleToggleCategory}
    />

  )}

</main>
</div>
  );
};

export default AdminPage;
